import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

const THEMES = [
  { value: 'baseball-manitoba', label: 'Baseball Manitoba', icon: 'light' },
  { value: 'baseball-manitoba-dark', label: 'Baseball Manitoba Dark', icon: 'dark' },
  //{ value: 'light', label: 'Light', icon: 'light' },
  //{ value: 'dark', label: 'Dark', icon: 'dark' },
] as const;

type ThemeValue = typeof THEMES[number]['value'];

const STORAGE_KEY = 'pitch-counter-theme';

@customElement('theme-switcher')
export class ThemeSwitcher extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private currentTheme: ThemeValue = 'baseball-manitoba';

  connectedCallback() {
    super.connectedCallback();
    this.loadTheme();
  }

  private loadTheme() {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeValue | null;
    const validThemes = THEMES.map(t => t.value);

    if (saved && validThemes.includes(saved as ThemeValue)) {
      this.currentTheme = saved as ThemeValue;
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.currentTheme = prefersDark ? 'baseball-manitoba-dark' : 'baseball-manitoba';
    }
    this.applyTheme();
  }

  private applyTheme() {
    document.documentElement.setAttribute('data-theme', this.currentTheme);
  }

  private setTheme(theme: ThemeValue) {
    this.currentTheme = theme;
    localStorage.setItem(STORAGE_KEY, theme);
    this.applyTheme();
  }

  private isDarkTheme(): boolean {
    return this.currentTheme.includes('dark');
  }

  private toggleTheme() {
    if (this.isDarkTheme()) {
      this.setTheme('baseball-manitoba');
    } else {
      this.setTheme('baseball-manitoba-dark');
    }
  }

  render() {
    return html`
      <div class="dropdown dropdown-end">
        <div tabindex="0" role="button" class="btn btn-ghost btn-sm gap-1" title="Theme">
          <!-- Sun/Moon toggle icon -->
          ${this.isDarkTheme() ? html`
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          ` : html`
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          `}
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <ul tabindex="0" class="dropdown-content z-50 menu p-2 shadow-lg bg-base-200 rounded-box w-56">
          ${THEMES.map(theme => html`
            <li>
              <button
                class=${this.currentTheme === theme.value ? 'active' : ''}
                @click=${() => this.setTheme(theme.value)}
              >
                ${theme.icon === 'dark' ? html`
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                ` : html`
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                `}
                <span>${theme.label}</span>
                ${this.currentTheme === theme.value ? html`
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                ` : ''}
              </button>
            </li>
          `)}
        </ul>
      </div>
    `;
  }
}
